import type { FollowUpKind, InteractionContext } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  EmptyState,
  ErrorState,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  SegmentedSwitch,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Mail, MessageCircle, Phone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { LogInteractionDialog, SendMessageDialog } from '@/features/crm/InteractionsPanel';
import { FOLLOW_UP_KINDS, phoneLink, whatsAppLink } from '@/features/crm/labels';
import { followUpsQueryOptions, segmentsQueryOptions } from '@/features/crm/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

interface FollowUpsSearch {
  kind?: FollowUpKind;
  segmentId?: string;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CONTEXT_OF: Record<FollowUpKind, InteractionContext> = {
  BIRTHDAYS: 'BIRTHDAY',
  INACTIVE: 'INACTIVE',
  NO_CONSENT: 'MANUAL',
  SEGMENT: 'SEGMENT',
};

export const Route = createFileRoute('/_app/crm/follow-ups')({
  validateSearch: (search: Record<string, unknown>): FollowUpsSearch => ({
    ...(FOLLOW_UP_KINDS.includes(search['kind'] as FollowUpKind)
      ? { kind: search['kind'] as FollowUpKind }
      : {}),
    ...(typeof search['segmentId'] === 'string' && UUID.test(search['segmentId'])
      ? { segmentId: search['segmentId'] }
      : {}),
  }),
  component: FollowUpsPage,
});

/** Listes d'action et relances individuelles (ADR-025, D-H67, R-CRM-05). */
function FollowUpsPage() {
  const { t, i18n } = useTranslation(['crm', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canRead = useCan('crm.read');
  const canSend = useCan('notification.send');
  const canLog = useCan('customer.update');
  const propertyId = useScopedPropertyId();
  const kind = search.kind ?? 'BIRTHDAYS';
  const segments = useQuery(segmentsQueryOptions(canRead));
  const segmentId = search.segmentId ?? segments.data?.[0]?.id;
  const query = useQuery(
    followUpsQueryOptions(
      { kind, segmentId: kind === 'SEGMENT' ? segmentId : undefined, propertyId, limit: 100 },
      canRead && (kind !== 'SEGMENT' || Boolean(segmentId)),
    ),
  );
  const [logFor, setLogFor] = useState<string | null>(null);
  const [emailFor, setEmailFor] = useState<{
    id: string;
    email: string | null;
    consent: boolean;
    templateId: string | undefined;
  } | null>(null);
  const setSearch = (next: Partial<FollowUpsSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });
  const money = (value: number) => formatAmount(value, i18n.language, 'XOF');

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:followUps.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  const rows = query.data?.rows ?? [];
  return (
    <Container>
      <PageHeader
        title={t('crm:followUps.title')}
        subtitle={t('crm:followUps.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:followUps.title') },
        ]}
      />
      <Card>
        <CardBody density="flush">
          <Toolbar>
            <SegmentedSwitch
              label={t('crm:followUps.title')}
              value={kind}
              onValueChange={(value) => setSearch({ kind: value })}
              wrap
              options={FOLLOW_UP_KINDS.map((value) => ({
                value,
                label: t(`crm:followUps.kinds.${value}`),
              }))}
            />
            {kind === 'SEGMENT' ? (
              <NativeSelect
                aria-label={t('crm:followUps.segment')}
                value={segmentId ?? ''}
                onChange={(event) => setSearch({ segmentId: event.target.value || undefined })}
              >
                <option value="">{t('crm:followUps.pickSegment')}</option>
                {(segments.data ?? []).map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
              </NativeSelect>
            ) : null}
            {query.data ? (
              <span className="muted t-13">
                {t('crm:followUps.total', { count: query.data.total })}
              </span>
            ) : null}
          </Toolbar>

          {query.isError ? (
            <ErrorState
              title={t('errors:generic.title')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:trace.copy')}
              traceCopiedLabel={t('common:trace.copied')}
            />
          ) : query.isPending ? (
            <TableSkeleton rows={5} columns={6} label={t('common:states.loading')} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('crm:followUps.empty')} />
          ) : (
            <TableWrap>
              <Table>
                <caption className="sr-only">{t('crm:followUps.title')}</caption>
                <thead>
                  <tr>
                    <Th>{t('crm:followUps.columns.customer')}</Th>
                    <Th>{t('crm:followUps.columns.date')}</Th>
                    <Th align="right">{t('crm:followUps.columns.spend')}</Th>
                    <Th>{t('crm:followUps.columns.consents')}</Th>
                    <Th>{t('crm:followUps.columns.lastContact')}</Th>
                    <Th>{t('crm:followUps.columns.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const whatsapp = row.consents.find(
                      (state) => state.channel === 'WHATSAPP',
                    )?.granted;
                    const email = row.consents.find((state) => state.channel === 'EMAIL')?.granted;
                    const message = row.suggestedMessage?.body ?? '';
                    return (
                      <Tr key={row.customer.id}>
                        <Td>
                          <Link
                            className="link-quiet strong"
                            to="/crm/customers/$customerId"
                            params={{ customerId: row.customer.id }}
                          >
                            {row.customer.displayName}
                          </Link>
                          <div className="muted t-12">
                            {row.customer.phonePrimary ?? ''}
                            {row.loyalty
                              ? ` · ${t('crm:pos.tierPoints', { tier: row.loyalty.tierName ?? '—', points: row.loyalty.balance })}`
                              : ''}
                          </div>
                        </Td>
                        <Td>
                          {row.date
                            ? formatDateShort(`${row.date}T00:00:00.000Z`, i18n.language)
                            : '—'}
                          {row.lastVisitAt && kind !== 'INACTIVE' ? (
                            <div className="muted t-12">
                              {t('crm:followUps.columns.lastVisit')} :{' '}
                              {formatDateShort(row.lastVisitAt, i18n.language)}
                            </div>
                          ) : null}
                        </Td>
                        <Td align="right" numeric>
                          {money(row.spendTotal)}
                        </Td>
                        <Td>
                          <div className="row row--wrap" style={{ gap: 4 }}>
                            {row.consents
                              .filter((state) => state.granted)
                              .map((state) => (
                                <Badge key={state.channel} tone="green">
                                  {t(`crm:consents.channels.${state.channel}`)}
                                </Badge>
                              ))}
                            {row.consents.every((state) => !state.granted) ? (
                              <span className="muted t-12">
                                {t('crm:followUps.consentMissing')}
                              </span>
                            ) : null}
                          </div>
                        </Td>
                        <Td>
                          {row.lastInteractionAt
                            ? formatDateShort(row.lastInteractionAt, i18n.language)
                            : '—'}
                        </Td>
                        <Td>
                          <div className="row row--wrap" style={{ gap: 4 }}>
                            {row.customer.phonePrimary && whatsapp ? (
                              <Button variant="secondary" size="sm" asChild>
                                <a
                                  href={whatsAppLink(row.customer.phonePrimary, message)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <MessageCircle className="ico ico-14" aria-hidden="true" />
                                  {t('crm:followUps.whatsapp')}
                                </a>
                              </Button>
                            ) : null}
                            {row.customer.phonePrimary ? (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={phoneLink(row.customer.phonePrimary)}>
                                  <Phone className="ico ico-14" aria-hidden="true" />
                                  {t('crm:followUps.call')}
                                </a>
                              </Button>
                            ) : null}
                            {canSend && row.customer.email && email ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setEmailFor({
                                    id: row.customer.id,
                                    email: row.customer.email,
                                    consent: Boolean(email),
                                    templateId: row.suggestedMessage?.templateId,
                                  })
                                }
                              >
                                <Mail className="ico ico-14" aria-hidden="true" />
                                {t('crm:followUps.email')}
                              </Button>
                            ) : null}
                            {canLog ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setLogFor(row.customer.id)}
                              >
                                {t('crm:followUps.log')}
                              </Button>
                            ) : null}
                          </div>
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>
      {rows.length > 0 && !rows[0]?.suggestedMessage ? (
        <p className="muted t-13 mt-3">{t('crm:followUps.noTemplate')}</p>
      ) : null}
      {logFor ? (
        <LogInteractionDialog
          customerId={logFor}
          open
          onOpenChange={(open) => !open && setLogFor(null)}
          defaultContext={CONTEXT_OF[kind]}
          defaultKind="WHATSAPP"
        />
      ) : null}
      {emailFor ? (
        <SendMessageDialog
          customerId={emailFor.id}
          email={emailFor.email}
          emailConsent={emailFor.consent}
          open
          onOpenChange={(open) => !open && setEmailFor(null)}
          defaultContext={CONTEXT_OF[kind]}
          defaultTemplateId={emailFor.templateId}
        />
      ) : null}
    </Container>
  );
}
